import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';

export function useProductStats() {
  return useQuery({
    queryKey: ['products', 'stats'],
    queryFn: async () => (await api.get('/api/products/stats')).data,
    staleTime: 30_000,
  });
}

export function useProducts(params) {
  const clean = Object.fromEntries(Object.entries(params).filter(([, v]) => v !== '' && v != null));
  const qs = new URLSearchParams(clean).toString();
  return useQuery({
    queryKey: ['products', 'list', clean],
    queryFn: async () => (await api.get(`/api/products?${qs}`)).data,
    keepPreviousData: true,
  });
}

// SKU catalog -> Xero Items sync (proxied to the tech server).
export function useXeroItemsStatus() {
  return useQuery({
    queryKey: ['xero', 'items-status'],
    queryFn: async () => (await api.get('/api/xero/items-status')).data,
    staleTime: 30_000,
  });
}

export function useSyncXero() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body) => (await api.post('/api/xero/sync-items', body || {})).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['products'] });
      qc.invalidateQueries({ queryKey: ['xero'] });
    },
  });
}

// Push a single SKU to Xero (per-row button). Takes the product id.
export function useSyncXeroItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => (await api.post(`/api/xero/sync-item/${id}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['products'] });
      qc.invalidateQueries({ queryKey: ['xero'] });
    },
  });
}

export function useUpdateProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, patch }) => (await api.patch(`/api/products/${id}`, patch)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['products'] }),
  });
}

export function useClearReview() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => (await api.post(`/api/products/${id}/clear-review`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['products'] }),
  });
}

// The storeroom catalogue: everything on the monthly count sheet, with its
// supplier quotes. Returns the unorderable ones too — items nobody has priced,
// and items whose quotes disagree on the unit — because those are the ones
// somebody has to do something about.
export function useConsumableCatalog(params) {
  const qs = new URLSearchParams(
    Object.entries(params || {}).filter(([, v]) => v !== '' && v != null)).toString();
  return useQuery({
    queryKey: ['products', 'consumables', qs],
    queryFn: async () => (await api.get(`/api/products/consumables${qs ? `?${qs}` : ''}`)).data,
    staleTime: 30000,
  });
}

// --- consumable supplier prices ------------------------------------------

export function useSuppliers() {
  return useQuery({
    queryKey: ['suppliers', 'active'],
    queryFn: async () => (await api.get('/api/products/consumables/suppliers')).data,
    staleTime: 300000,
  });
}

// One invalidation for all three writes: every one of them changes whether the
// item is orderable, which is the column the panel is really about.
function useQuoteMutation(fn) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['products', 'consumables'] }),
  });
}

export function useAddConsumableQuote() {
  return useQuoteMutation(async ({ itemId, ...body }) =>
    (await api.post(`/api/products/consumables/${itemId}/suppliers`, body)).data);
}

export function useSaveConsumableQuote() {
  return useQuoteMutation(async ({ id, ...body }) =>
    (await api.patch(`/api/products/consumables/suppliers/${id}`, body)).data);
}

export function useRemoveConsumableQuote() {
  return useQuoteMutation(async ({ id }) =>
    (await api.delete(`/api/products/consumables/suppliers/${id}`)).data);
}

// --- purchasing catalogue -> storeroom -----------------------------------

export function useStoreroomLists() {
  return useQuery({
    queryKey: ['products', 'storeroom-lists'],
    queryFn: async () => (await api.get('/api/products/storeroom-lists')).data,
    staleTime: 600000,
  });
}

export function useSendToStoreroom() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, category }) =>
      (await api.post(`/api/products/${id}/send-to-storeroom`, { category })).data,
    // The consumables panel is the other view of the same fact, so it has to
    // be refetched or the item appears to have gone nowhere.
    onSuccess: () => qc.invalidateQueries({ queryKey: ['products', 'consumables'] }),
  });
}
